import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { ColumnEditorActionSpec, ColumnSpec, ColumnType, ProjectionSpec, Row, ScriptState, Table } from '@easydb/shared';
import { scriptState } from '@easydb/shared';
import { getContext } from '../app-context.js';
import { materialIconStyles } from '../chrome/material-icon-css.js';
import { cryptoUUID, slugTable } from '../util/ids.js';
import { ctrlEnterSubmits, dialogChromeStyles, HostDialogs, makeDialogDraggable } from '@marccawood/lit-dialogs';
import { markDirty, watchDialogDirty } from '../chrome/dirty-guard.js';
import { ScriptEditorDialog } from './script-editor-dialog.js';
import { allColumnsFlagged, buildColumnSpec, toggleColumnFlag, type ColumnFlag, type ColumnRow } from './column-row.js';
import { isErrorField } from '../table/row-errors.js';
import { renameRowFields, type FieldRename } from '../table/column-merge.js';
import { conditionalPatch, constraintErrorMessage, draftProblem, migrateRowData, planColumnChanges, saveStageError, tightenedConstraints, type SaveStage } from '../table/table-save.js';
import { createValidator, issueMessages } from '../table/validate-rules.js';
import { readRows } from '../db/row-reader.js';
import { offerableRenderers, rendererOptionsFor } from '../table/renderer-options.js';
import { PREVIEW_ROWS } from '../table/column-preview.js';
import './column-preview-table.js';
import type { PreviewState } from './column-preview-table.js';
import {
  describeReferences,
  findTableReferences,
  renameProjectionOutputs,
  renameProjectionSourceFields,
  renameViewMappings,
  repointProjectionSpec,
  specOf,
  type TableReferences,
} from '../table/table-references.js';

const TYPE_OPTIONS: ColumnType[] = ['string', 'text', 'number', 'boolean', 'date', 'datetime', 'array'];

/**
 * What each of the two script buttons says in each of its three states.
 *
 * The `off` wording names the consequence rather than the setting — "kept but
 * not run" is the thing a user hovering a red button needs to know, and it is
 * the answer to the question the red is provoking.
 */
const SCRIPT_TITLE: Record<ScriptState, string> = {
  none: 'Add a script: render(row) computes what this column displays',
  on: 'Edit the script — its render(row) output is what this column displays',
  off: 'This column’s script is switched off — kept, but not run. Click to edit or switch it back on',
};

const VALIDATE_TITLE: Record<ScriptState, string> = {
  none: 'Add validation: validate(value, row) throws to reject a manual cell edit',
  on: 'Edit the validation — validate(value, row) throws to reject a manual cell edit',
  off: 'This column’s validation is switched off — kept, but not enforced. Click to edit or switch it back on',
};

/**
 * Warn before a rename that will move name-based references with it.
 *
 * Falls back to the native confirm if the host dialog element is not mounted —
 * this is a decision the user must actually get to make, so it must not be
 * skipped just because the shell has not rendered.
 */
/**
 * Carry every name-based reference across to the new name.
 *
 * Nothing else will ever repair these links: unlike a delete-and-recreate, the
 * table does not come back under the old name, so a reference left pointing at
 * it resolves to nothing for good.
 */
async function repointReferences(tableId: string, oldName: string, name: string, refs: TableReferences | null): Promise<void> {
  const ctx = await getContext();
  // Views bind by name AND by id, so catch both: one bound by id alone (never
  // renamed before) still needs its snapshot brought up to date.
  const insts = (await ctx.store.viewInstances.find()).filter((vi) => vi.tableId === tableId || vi.tableName === oldName);
  for (const vi of insts) {
    if (vi.tableName !== name) {
      await ctx.store.viewInstances.patch(vi.id, { tableName: name, updatedAt: Date.now() });
    }
  }
  // Projections name their sources; rewrite the ones that named this table.
  for (const p of refs?.projections ?? []) {
    const spec = specOf(p);
    const next = spec && repointProjectionSpec(spec, oldName, name);
    if (!next) continue;
    await ctx.store.tables.patch(p.id, {
      source: { type: 'projection', config: next as unknown as Record<string, unknown> },
      updatedAt: Date.now(),
    });
  }
}

/**
 * Carry field renames into every projection that names one of the renamed
 * fields — the projection being edited (its OUTPUT fields) and any projection
 * reading FROM this table (its source fields and join keys).
 *
 * Without this, renaming a field emptied a column: the projection still wrote
 * the old key into every row while the renamed ColumnSpec read the new one. A
 * join key left on the old name matched nothing at all.
 *
 * `tableName` is the name the specs currently use — the name BEFORE a rename in
 * the same save, which `repointReferences` fixes afterwards.
 */
async function repointFieldRenames(tableId: string, tableName: string, renames: readonly FieldRename[], tables: Table[]): Promise<void> {
  if (renames.length === 0) return;
  const ctx = await getContext();
  // Views and visualizations bind to columns by name too — see
  // `renameViewMappings`. Done first so a failure writing a projection cannot
  // leave the views behind (both are independent patches either way).
  const views = (await ctx.store.viewInstances.find({ workspaceId: ctx.workspaceId })).filter((v) => v.tableId === tableId);
  for (const v of views) {
    const patch = renameViewMappings(v, renames);
    if (patch) await ctx.store.viewInstances.patch(v.id, { ...patch, updatedAt: Date.now() });
  }
  if (views.length > 0) {
    // Push the change into any open window or docked pane, so a chart re-reads
    // its mapping instead of showing "no column mapped" until the next reload.
    document.dispatchEvent(new CustomEvent('easydb:reload-views'));
  }
  const write = async (t: Table, next: ProjectionSpec) => {
    await ctx.store.tables.patch(t.id, {
      source: { type: 'projection', config: next as unknown as Record<string, unknown> },
      updatedAt: Date.now(),
    });
  };
  for (const t of tables) {
    const spec = specOf(t);
    if (!spec) continue;
    // The projection being edited: its own output fields were renamed.
    const next = t.id === tableId ? renameProjectionOutputs(spec, renames) : renameProjectionSourceFields(spec, tableName, renames);
    if (next) await write(t, next);
  }
}

function confirmRename(from: string, to: string, what: string): Promise<boolean> {
  const message = `Renaming "${from}" to "${to}" affects ${what}.\n\n` + `They reference this table by name, so they will be updated to point at "${to}". Continue?`;
  const host = HostDialogs.instance;
  return host ? host.confirm(message, 'Rename table') : Promise.resolve(window.confirm(message));
}

/**
 * Dual-purpose dialog: creates new tables and edits the columns of existing
 * ones. Open mode is chosen by the optional tableId argument to open().
 *
 * Edit mode keeps existing field names intact by default. Renaming a field
 * is allowed; on save every row's `data` object is re-keyed (see
 * `renameRowFields` in `table/column-merge.ts`) so existing values follow
 * the field to its new name.
 */
@customElement('new-table-dialog')
export class NewTableDialog extends LitElement {
  static override styles = [
    materialIconStyles,
    dialogChromeStyles,
    css`
      dialog {
        max-width: 96vw;
        width: 1180px;
      }
      label {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.85rem;
        color: #374151;
      }
      /* A checkbox reads as one line with its text, not stacked above it. */
      label.inline {
        flex-direction: row;
        align-items: center;
        gap: 0.4rem;
      }
      label.inline input[type='checkbox'] {
        width: auto;
        margin: 0;
      }
      input,
      select {
        font: inherit;
        padding: 0.4rem 0.5rem;
        border: 1px solid #d1d5db;
        border-radius: 0.25rem;
      }
      .columns {
        display: grid;
        gap: 0.5rem;
      }
      .col-header,
      .col-row {
        display: grid;
        /* drag | 👁 | field | label | type | renderer | script | max | validate | U ! ⇅ ⚲ | filter | up down del */
        grid-template-columns:
          1.25rem 1.5rem 1fr 1fr 7rem 7rem 1.5rem 4rem 1.5rem 1.5rem 1.5rem 1.5rem 1.5rem 1.5rem
          1.5rem 1.5rem 1.5rem;
        gap: 0.4rem;
        align-items: center;
      }
      .drag-handle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #9ca3af;
        cursor: grab;
        user-select: none;
      }
      .drag-handle:active {
        cursor: grabbing;
      }
      .drag-handle:hover {
        color: #374151;
      }
      .col-row.drag-source {
        opacity: 0.4;
      }
      .col-row.drop-before {
        box-shadow: inset 0 3px 0 #3b82f6;
      }
      .col-row.drop-after {
        box-shadow: inset 0 -3px 0 #3b82f6;
      }
      .col-row input[type='number'] {
        width: 100%;
        box-sizing: border-box;
      }
      .col-row .flag {
        display: inline-flex;
        justify-content: center;
      }
      .col-header .flag-label {
        font-size: 0.7rem;
        text-align: center;
      }
      /* A header that toggles its whole column. Styled to sit in the header row
         like the plain labels beside it — the hover and the pointer are what say
         it does something. */
      .col-header button.flag-head {
        background: transparent;
        border: 0;
        padding: 0;
        color: inherit;
        font: inherit;
        font-size: 0.7rem;
        cursor: pointer;
        line-height: 1;
      }
      .col-header button.flag-head:hover {
        color: #374151;
      }
      .col-header button.flag-head:focus-visible {
        outline: 2px solid #3b82f6;
        outline-offset: 1px;
      }
      .col-header {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #6b7280;
      }
      button.icon-btn {
        background: transparent;
        border: 0;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        font-size: 1rem;
      }
      button.icon-btn:hover:not(:disabled) {
        color: #111827;
      }
      button.icon-btn:disabled {
        color: #d1d5db;
        cursor: not-allowed;
      }
      /* Both script buttons are TRI-STATE, and both use the same three colours,
       because they answer the same question about two different scripts:

         gray (the default .icon-btn colour) — no script on this column
         blue                                — a script that runs
         red                                 — a script that is KEPT but parked

       The two used to be the same pencil in adjacent columns, told apart only by
       blue-vs-amber, which read as one wide control; the validation button now
       carries its own glyph (the "rule" icon) so the colours are free to mean state and
       nothing else. Red is the load-bearing one: a rule that exists and is doing
       nothing is invisible in every other part of the app, and the alternative to
       showing it here is a user writing the same rule a second time. */
      button.icon-btn.is-on {
        color: #2563eb;
      }
      /* Without this, the plain :hover rule above (color: #111827) would win and
       a set script's button would go near-black on hover, losing the state cue.
       A darker shade keeps hover feedback without discarding it. */
      button.icon-btn.is-on:hover:not(:disabled) {
        color: #1d4ed8;
      }
      button.icon-btn.is-off {
        color: #dc2626;
      }
      button.icon-btn.is-off:hover:not(:disabled) {
        color: #b91c1c;
      }
      /* The funnel that says this column carries a filter. Blue while the filter
       is on, faded once it is switched off — the glyph changes too, because the
       state has to survive a screenshot in grey and a colour-blind reader. */
      button.icon-btn.filter-btn.on {
        color: #2563eb;
      }
      button.icon-btn.filter-btn.on:hover:not(:disabled) {
        color: #1d4ed8;
      }
      button.icon-btn.filter-btn.off {
        color: #9ca3af;
      }
      .col-header .filter-head {
        color: #9ca3af;
        line-height: 1;
      }
      button.row-del {
        color: #9ca3af;
        font-size: 1.1rem;
      }
      button.row-del:hover:not(:disabled) {
        color: #ef4444;
      }
      /* The removed-columns offer: chips on one wrapping line under the list. */
      .deleted-cols {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.4rem;
        margin-top: 0.35rem;
      }
      .deleted-cols button.add {
        padding: 0.2rem 0.5rem;
        font-size: 0.85rem;
      }
      button.add {
        align-self: start;
        background: #f3f4f6;
        border: 1px dashed #9ca3af;
        padding: 0.4rem 0.75rem;
        border-radius: 0.25rem;
        cursor: pointer;
      }
      /* A banner at the TOP of the body, the same shape as the notice above it.
         It used to be bare red text at the very bottom, below every column row
         and the preview — on a table with a dozen columns the body scrolls, so
         pressing Save appeared to do nothing at all. Same reason it is a filled
         box: this message is the one thing between the user and a saved table.
         No backticks in this comment: it sits in a tagged template literal. */
      .error {
        background: #fef2f2;
        border: 1px solid #fca5a5;
        color: #991b1b;
        border-radius: 0.35rem;
        padding: 0.45rem 0.6rem;
        font-size: 0.85rem;
        /* The constraint report is several lines joined with \n. */
        white-space: pre-line;
      }
      .notice {
        background: #fef9c3;
        border: 1px solid #fde047;
        color: #713f12;
        border-radius: 0.35rem;
        padding: 0.45rem 0.6rem;
        font-size: 0.85rem;
        margin-bottom: 0.6rem;
      }
      .hint {
        color: #6b7280;
        font-size: 0.78rem;
      }
      /* Two panes with a draggable edge between them. The BODY used to be the
         scroller, which meant the preview could only ever be as tall as its own
         max-height and dragging its grip would have made the dialog longer
         instead of giving the preview more of it. Now the body is the frame: the
         column list takes what the preview does not. The error banner stays
         outside the pane, pinned - it is the one message that must not scroll
         away. No backticks in this comment: it sits in a tagged template. */
      .dialog-body {
        overflow: hidden;
        min-height: 0;
      }
      .editor-pane {
        flex: 1 1 auto;
        min-height: 5rem;
        overflow: auto;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      .mi.sm {
        font-size: 0.95rem;
      }
    `,
  ];

  @state() private mode: 'new' | 'edit' = 'new';
  @state() private editTableId: string | null = null;
  @state() private name = '';
  @state() private tableTitle = '';
  /** Table-level read-only flag (`Table.readonly`) — no editors, no add/delete row. */
  @state() private tableReadonly = false;
  /**
   * Names the user removed from this table before (`Table.deletedColumns`), so a
   * refresh does not re-add them. Offered back at the bottom of the editor: a
   * column deleted by mistake was otherwise only recoverable by typing its name
   * from memory, and the app knew it all along.
   */
  @state() private deletedFields: string[] = [];
  @state() private columns: ColumnRow[] = [];
  @state() private errorMsg = '';
  /** Non-error banner (e.g. "a refresh found new columns — review them"). */
  @state() private noticeMsg = '';
  @state() private dragSrcIdx: number | null = null;
  @state() private dropTargetIdx: number | null = null;
  @state() private dropEdge: 'before' | 'after' | null = null;
  /**
   * The table's per-column filters (`Table.filters`) as the editor holds them.
   *
   * They are shown here because a HIDDEN column's filter is otherwise out of
   * reach: no header means no funnel, so the grid keeps narrowing and the only
   * thing that would say why is not on screen. Written back on Save, like every
   * other change in this dialog.
   */
  @state() private filters: Record<string, string> = {};
  /**
   * Filters switched off in this session, keyed by field, holding the expression
   * so a second click puts it back. Switching one off must not cost the user the
   * expression they built — that is what makes it a toggle rather than a delete.
   */
  @state() private filtersOff: Record<string, string> = {};
  /** First {@link PREVIEW_ROWS} rows of the table being edited; edit mode only. */
  @state() private previewRows: Row[] = [];
  /**
   * Why the preview has no rows. A read still running, a read that failed and a
   * table that is genuinely empty all showed the same "No rows to preview" —
   * which reads as "this table has no data", the one thing it did not mean.
   */
  @state() private previewState: PreviewState = 'none';
  /**
   * What the failed read said. Shown with the failure: "could not be read" on its
   * own leaves the user with nothing to act on, and the reason is usually the
   * whole story (a permission gone, a source offline).
   */
  @state() private previewError: string | null = null;
  /**
   * Answers arriving for a table the dialog has since been reopened on are
   * dropped. A slow read outliving its dialog is exactly the case here.
   */
  private previewToken = 0;
  /**
   * Snapshot of renderer names registered at open time. Populated from
   * `registries.cellRenderers` keys; built-in renderers (date, datetime,
   * boolean, color, image, link) and any plugin-registered ones land here.
   * Names kept only for older columns are left out — see LEGACY_CELL_RENDERERS.
   */
  @state() private rendererOptions: string[] = [];
  /**
   * Plugin-registered buttons that rewrite the columns being edited (e.g. the
   * auto-renderer's "Guess renderers"). Snapshotted on open, like the renderer
   * options above.
   */
  @state() private columnActions: ColumnEditorActionSpec[] = [];
  private rendererSubUnsub?: (() => void) | undefined;

  private dialogEl: HTMLDialogElement | null = null;

  override firstUpdated() {
    this.dialogEl = this.shadowRoot?.querySelector('dialog') ?? null;
    const header = this.shadowRoot?.querySelector('.dialog-header') as HTMLElement | null;
    if (this.dialogEl && header) makeDialogDraggable(this.dialogEl, header);
    // Half-defined columns must not be lost to a reload — see dirty-guard.
    if (this.dialogEl) watchDialogDirty('columns-editor', this.dialogEl);
  }

  /**
   * Open the dialog. If tableId is provided, opens in "edit" mode and pre-fills
   * the form from the saved Table. Otherwise opens "new" mode with two default
   * string columns.
   */
  async open(tableId?: string, opts?: { notice?: string | undefined }): Promise<void> {
    this.errorMsg = '';
    this.noticeMsg = opts?.notice ?? '';
    // Snapshot the currently registered cell renderers and keep them fresh
    // while the dialog is open — a plugin install after dialog open should
    // surface its renderer too. The unsub fires on close().
    const ctxForRenderers = await getContext();
    this.rendererOptions = offerableRenderers(ctxForRenderers.registries.cellRenderers);
    this.rendererSubUnsub?.();
    this.columnActions = [...ctxForRenderers.registries.columnEditorActions];
    this.rendererSubUnsub = ctxForRenderers.events.on('app:ready', () => {
      this.rendererOptions = offerableRenderers(ctxForRenderers.registries.cellRenderers);
      this.columnActions = [...ctxForRenderers.registries.columnEditorActions];
    });
    if (tableId) {
      const ctx = await getContext();
      const t = await ctx.store.tables.findOne(tableId);
      if (!t) return;
      this.mode = 'edit';
      this.editTableId = tableId;
      this.name = t.name;
      this.tableTitle = t.title ?? '';
      this.tableReadonly = !!t.readonly;
      this.deletedFields = [...(t.deletedColumns ?? [])];
      this.columns = t.columns.map((c) => ({
        field: c.field,
        label: c.label,
        type: c.type,
        renderer: c.renderer,
        script: c.script,
        scriptActive: c.scriptActive,
        validate: c.validate,
        validateActive: c.validateActive,
        max: c.max,
        unique: c.unique,
        notnull: c.notnull,
        hidden: c.hidden,
        sortable: c.sortable,
        filterable: c.filterable,
        origField: c.field,
        orig: c,
      }));
      // The preview is loaded AFTER the dialog is on screen, not before. It used
      // to be awaited here, over a read of the WHOLE table — seconds and ~15 MB
      // for a 609k-row one — so the editor either arrived long after the click or,
      // when that read failed, arrived claiming the table had no rows. We still
      // don't subscribe: the dialog is short-lived and a snapshot is fine.
      this.filters = { ...(t.filters ?? {}) };
      this.filtersOff = {};
      void this.loadPreview(tableId, t.columns);
    } else {
      this.mode = 'new';
      this.editTableId = null;
      this.name = '';
      this.tableTitle = '';
      this.tableReadonly = false;
      this.deletedFields = [];
      this.columns = [
        { field: 'name', label: 'Name', type: 'string' },
        { field: 'note', label: 'Note', type: 'string' },
      ];
      this.previewRows = [];
      this.previewState = 'none';
      this.previewToken++; // a read still running belongs to the table just left
      this.filters = {};
      this.filtersOff = {};
    }
    await this.updateComplete;
    this.dialogEl?.showModal();
  }

  /**
   * Fill the live preview, asking for {@link PREVIEW_ROWS} rows rather than all
   * of them.
   *
   * `readRows` is used instead of `find()` so a backend that can narrow does:
   * the SQLite store turns the limit into `LIMIT` and answers in milliseconds,
   * and the cap bounds the read for a routed collection that cannot narrow at
   * all. A failure is REPORTED rather than shown as an empty
   * table — the column settings still save either way, so the editor stays
   * usable without its preview.
   */
  private async loadPreview(tableId: string, columns: readonly ColumnSpec[]): Promise<void> {
    const token = ++this.previewToken;
    this.previewRows = [];
    this.previewState = 'loading';
    this.previewError = null;
    try {
      const ctx = await getContext();
      const page = await readRows(ctx.store.rows(tableId), { columns, limit: PREVIEW_ROWS }, PREVIEW_ROWS);
      if (token !== this.previewToken) return;
      this.previewRows = page.rows;
      this.previewState = 'ready';
    } catch (err) {
      if (token !== this.previewToken) return;
      this.previewRows = [];
      this.previewState = 'error';
      this.previewError = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.warn('[columns-editor] the preview rows could not be read', err);
    }
  }

  private close(): void {
    this.dialogEl?.close();
    this.rendererSubUnsub?.();
    this.rendererSubUnsub = undefined;
  }

  /**
   * Adding, removing and reordering a column happen on a button click or a
   * drag, neither of which fires the `input`/`change` `watchDialogDirty`
   * listens for. Without this, deleting three columns and hitting reload lost
   * them with no warning.
   */
  private touchDirty(): void {
    if (this.dialogEl) markDirty('columns-editor', this.dialogEl);
  }

  private addColumn(): void {
    const i = this.columns.length + 1;
    this.columns = [...this.columns, { field: `field_${i}`, label: `Field ${i}`, type: 'string' }];
    this.touchDirty();
  }

  private removeColumn(idx: number): void {
    this.columns = this.columns.filter((_, i) => i !== idx);
    this.touchDirty();
  }

  /**
   * Switch a column's filter off, or back on. The expression is kept either way
   * and nothing is written until Save, so this is as reversible as every other
   * edit in the dialog — Cancel leaves the filter exactly as it was.
   */
  private toggleFilter(field: string): void {
    const active = this.filters[field];
    const stashed = this.filtersOff[field];
    const filters = { ...this.filters };
    const off = { ...this.filtersOff };
    if (active !== undefined) {
      delete filters[field];
      off[field] = active;
    } else if (stashed !== undefined) {
      delete off[field];
      filters[field] = stashed;
    } else {
      return; // no filter on this column: the button is not rendered
    }
    this.filters = filters;
    this.filtersOff = off;
    this.touchDirty();
  }

  private moveColumn(idx: number, delta: -1 | 1): void {
    const j = idx + delta;
    if (j < 0 || j >= this.columns.length) return;
    const next = [...this.columns];
    const [item] = next.splice(idx, 1);
    next.splice(j, 0, item!);
    this.columns = next;
    this.touchDirty();
  }

  private onRowDragStart(e: DragEvent, idx: number) {
    this.dragSrcIdx = idx;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/x-easydb-coleditor-row', String(idx));
    }
  }

  private onRowDragOver(e: DragEvent, idx: number, row: HTMLElement) {
    if (this.dragSrcIdx === null || this.dragSrcIdx === idx) return;
    e.preventDefault();
    const rect = row.getBoundingClientRect();
    const before = e.clientY < rect.top + rect.height / 2;
    this.dropTargetIdx = idx;
    this.dropEdge = before ? 'before' : 'after';
  }

  private onRowDragLeave(idx: number) {
    if (this.dropTargetIdx === idx) {
      this.dropTargetIdx = null;
      this.dropEdge = null;
    }
  }

  private onRowDrop(e: DragEvent, targetIdx: number) {
    e.preventDefault();
    const src = this.dragSrcIdx;
    const edge = this.dropEdge;
    this.dragSrcIdx = null;
    this.dropTargetIdx = null;
    this.dropEdge = null;
    if (src === null || src === targetIdx || !edge) return;

    const next = [...this.columns];
    const [moved] = next.splice(src, 1);
    // After splice, the target index shifts left by 1 when src < target.
    let toIdx = targetIdx + (src < targetIdx ? -1 : 0);
    if (edge === 'after') toIdx += 1;
    next.splice(toIdx, 0, moved!);
    this.columns = next;
    this.touchDirty();
  }

  private onRowDragEnd() {
    this.dragSrcIdx = null;
    this.dropTargetIdx = null;
    this.dropEdge = null;
  }

  private patchColumn(idx: number, patch: Partial<ColumnRow>): void {
    this.columns = this.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    this.touchDirty();
  }

  /**
   * A clickable header for one checkbox column: click ticks every box, click
   * again clears them.
   *
   * A button rather than a styled span, because the header is now a control and
   * has to be reachable by keyboard and announced as one — a 40-column table is
   * exactly where hiding all but three columns by hand is most tedious, so this
   * is the shortcut those users need most.
   */
  private renderFlagHead(flag: ColumnFlag, glyph: string, label: string) {
    const all = allColumnsFlagged(this.columns, flag);
    return html`<button
      type="button"
      class="flag-label flag-head"
      title=${`${label} — click to ${all ? 'clear' : 'set'} every column`}
      aria-pressed=${all ? 'true' : 'false'}
      @click=${() => {
        this.columns = toggleColumnFlag(this.columns, flag);
        this.touchDirty();
      }}
    >
      ${glyph}
    </button>`;
  }

  /**
   * Run a plugin's column-editor action on the current draft and take its result
   * back into the editor. Nothing is saved: the user sees the change in the rows
   * and still has to press Save (or close the dialog to drop it).
   *
   * The action gets the draft as ColumnSpecs, so it never has to know about the
   * editor's row shape. Coming back, each returned spec is matched to its row by
   * FIELD NAME — an action edits columns, it does not reorder or add them, and
   * matching by name keeps a row's `orig`/`origField` (the rename tracking and
   * the untouched-fields base) intact.
   */
  private async runColumnAction(action: ColumnEditorActionSpec): Promise<void> {
    this.errorMsg = '';
    const ctx = await getContext();
    try {
      const next = await action.run(ctx.api, {
        columns: this.columns.map((c) => buildColumnSpec(c)),
        ...(this.editTableId ? { tableId: this.editTableId } : {}),
      });
      if (!next) return;
      const byField = new Map(next.map((c) => [c.field, c]));
      this.columns = this.columns.map((row) => {
        const spec = byField.get(row.field);
        if (!spec) return row;
        return {
          ...row,
          label: spec.label ?? row.label,
          type: spec.type ?? row.type,
          renderer: spec.renderer,
          script: spec.script,
          scriptActive: spec.scriptActive,
          validate: spec.validate,
          validateActive: spec.validateActive,
        };
      });
      this.touchDirty();
    } catch (err) {
      this.errorMsg = `${action.label} failed: ${(err as Error).message}`;
    }
  }

  /**
   * Open the script-editor modal for the column at `idx`. Resolves to a
   * patched column row (or no-op on cancel). The dialog is mounted as a
   * sibling of this dialog in `<app-shell>`'s shadow root, so the static
   * `ScriptEditorDialog.instance` accessor is how we reach it without
   * leaking a query selector across shadow boundaries.
   */
  private async editScript(idx: number): Promise<void> {
    const dlg = ScriptEditorDialog.instance;
    if (!dlg) return;
    const c = this.columns[idx];
    if (!c) return;
    if (await this.refuseErrorColumn(c)) return;
    // The target is what lets the editor offer Run. Only a SAVED column of a
    // SAVED table has cells to write: a field being renamed in this editor is
    // not yet the key the rows are stored under, so Run would write a column
    // that does not exist until the editor saves.
    // `origField`, not `field`: Run writes to the key the ROWS use, which a
    // rename in this editor has not moved yet — the save re-keys them after.
    // Absent in "new table" mode, where there is nothing to write to.
    const saved = this.editTableId && c.origField ? { tableId: this.editTableId, field: c.origField } : undefined;
    // A column TYPED here but not saved yet has no target — so hand the editor a
    // way to make one. Only in edit mode: in "new table" mode there is no table
    // and no rows, so there is nothing a Run could write to whatever we saved.
    const commit =
      !saved && this.editTableId
        ? async (text: string, active: boolean): Promise<{ tableId: string; field: string } | null> => {
            // The script first: committing without it would add an empty column
            // and then run a script the table does not have.
            this.patchColumn(idx, { script: text.trim() ? text : undefined, scriptActive: active ? undefined : false });
            return await this.commitForRun(idx);
          }
        : undefined;
    const next = await dlg.open(c.script ?? '', c.label || c.field, 'render', {
      target: saved,
      active: c.scriptActive !== false,
      // The same rows the preview below the column list is drawing, so the
      // editor's verdict and the preview's error chips cannot disagree.
      previewRows: this.previewRows,
      commit,
    });
    if (next === null) return;
    // Both halves come back together, so ticking the box IS a save — there is no
    // state where the editor says "off" and the column still runs.
    this.patchColumn(idx, { script: next.text.trim() ? next.text : undefined, scriptActive: next.active ? undefined : false });
  }

  /**
   * Save the table NOW, from inside the script editor, so a column that has only
   * just been typed has cells to write to.
   *
   * It is the SAME save the dialog's own button does — the whole draft, not just
   * this column. A save of one column would be a second, quieter write path with
   * its own rules about renames and constraints, and the two would drift; and a
   * user who has typed a script has usually typed the column's type and flags
   * too, all of which the run should see.
   *
   * Returns where Run should write, or null when the save was refused — the
   * reason is on the dialog's own error banner, which is where the same refusal
   * from the Save button appears.
   */
  private async commitForRun(idx: number): Promise<{ tableId: string; field: string } | null> {
    const tableId = this.editTableId;
    const c = this.columns[idx];
    if (!tableId || !c) return null;

    const name = this.name.trim();
    const ctx = await getContext();
    const workspaceTables = (await ctx.store.tables.find()).filter((t) => t.workspaceId === ctx.workspaceId);
    const problem = draftProblem({ name, fields: this.columns.map((x) => x.field), tables: workspaceTables, selfId: tableId });
    if (problem) {
      this.errorMsg = problem;
      return null;
    }

    // Read the renames BEFORE the save: afterwards every column's `origField` is
    // its current field, which is exactly what makes the pending-rename list
    // empty again.
    const renames = this.fieldRenames();
    const columns = this.columns.map(buildColumnSpec);
    if (!(await this.applyTableEdit(tableId, name, columns, workspaceTables))) return null;
    this.adoptSaved(renames, columns);
    return { tableId, field: c.field.trim() };
  }

  /**
   * Re-base the draft on what was just written, so the dialog can stay open.
   *
   * Without this a second save would try to apply the same renames again —
   * `origField` still naming a column the store no longer has — and the filter
   * maps, which are keyed by the saved name, would point at the old keys.
   *
   * Cheaper and more predictable than re-reading the table: the save is what
   * decided these values, so adopting them is the truth by construction, and a
   * re-read would also throw away any edit made while it was in flight.
   */
  private adoptSaved(renames: readonly FieldRename[], columns: readonly ColumnSpec[]): void {
    const moved = new Map(renames.map((r) => [r.from, r.to]));
    const rekey = (m: Record<string, string>): Record<string, string> => Object.fromEntries(Object.entries(m).map(([k, v]) => [moved.get(k) ?? k, v]));
    this.filters = rekey(this.filters);
    this.filtersOff = rekey(this.filtersOff);
    const specs = new Map(columns.map((s) => [s.field, s]));
    this.columns = this.columns.map((c) => {
      const field = c.field.trim();
      const spec = specs.get(field);
      return { ...c, field, origField: field, ...(spec ? { orig: spec } : {}) };
    });
  }

  /**
   * Refuse both script editors on Validate's own `_error` column, and say why.
   *
   * An alert rather than a disabled button: the reason is one sentence and the
   * way out (rename the column) is not guessable from a greyed-out pencil.
   * Returns true when the caller should stop.
   */
  private async refuseErrorColumn(c: ColumnRow): Promise<boolean> {
    if (!isErrorField(c.field)) return false;
    const dialogs = HostDialogs.instance;
    await dialogs?.alert(
      'Validate owns the “_error” column — it rewrites every value in it on each run, so a script here would be overwritten and a rule here would be judging Validate’s own output. Rename the column to make it yours; the next run creates a fresh _error beside it.',
      'Scripts',
    );
    return true;
  }

  /**
   * Same modal, the column's OTHER script: the `validate(value, row)` rule run
   * before a manual cell edit is written.
   */
  private async editValidate(idx: number): Promise<void> {
    const dlg = ScriptEditorDialog.instance;
    if (!dlg) return;
    const c = this.columns[idx];
    if (!c) return;
    if (await this.refuseErrorColumn(c)) return;
    const next = await dlg.open(c.validate ?? '', c.label || c.field, 'validate', { active: c.validateActive !== false });
    if (next === null) return;
    this.patchColumn(idx, { validate: next.text.trim() ? next.text : undefined, validateActive: next.active ? undefined : false });
  }

  /**
   * Save, in four visible stages: validate → apply the table edit → migrate the
   * rows → repoint everything that names what just changed.
   *
   * The stages are separate because they are separate WRITES. There is no
   * transaction around a save, so the honest thing this method can do is say
   * which of them landed when one fails — see {@link runStages}.
   */
  private async submit(e: Event): Promise<void> {
    e.preventDefault();
    const name = this.name.trim();
    const ctx = await getContext();
    const workspaceTables = (await ctx.store.tables.find()).filter((t) => t.workspaceId === ctx.workspaceId);
    const problem = draftProblem({ name, fields: this.columns.map((c) => c.field), tables: workspaceTables, selfId: this.editTableId });
    if (problem) {
      this.errorMsg = problem;
      return;
    }
    // buildColumnSpec spreads each row's `orig` ColumnSpec (when hydrated from
    // a saved table) as the base, so fields the editor doesn't own — default,
    // width, description, units — survive the save instead of being dropped.
    // See column-row.ts for why clearing a field must explicitly delete it
    // rather than just skip setting it.
    const columns: ColumnSpec[] = this.columns.map(buildColumnSpec);
    const editId = this.mode === 'edit' ? this.editTableId : null;
    const saved = editId ? await this.applyTableEdit(editId, name, columns, workspaceTables) : await this.createTable(name, columns);
    if (saved) this.close();
  }

  /**
   * Run the save's writes one at a time, stopping at the first failure and
   * naming what had already been written.
   *
   * Returns false when a stage threw, so the dialog stays open on its error
   * banner instead of closing over a half-applied change.
   */
  private async runStages(stages: readonly SaveStage[]): Promise<boolean> {
    const done: string[] = [];
    for (const [what, run] of stages) {
      try {
        await run();
      } catch (err) {
        this.errorMsg = saveStageError(what, done, err);
        return false;
      }
      done.push(what);
    }
    return true;
  }

  private async createTable(name: string, columns: ColumnSpec[]): Promise<boolean> {
    const ctx = await getContext();
    return this.runStages([
      [
        'the new table',
        () =>
          ctx.store.tables.insert({
            id: cryptoUUID(),
            workspaceId: ctx.workspaceId,
            name,
            title: this.tableTitle.trim(),
            code: slugTable(name),
            columns,
            view: 'table',
            updatedAt: Date.now(),
          }),
      ],
    ]);
  }

  /**
   * Edit mode's save. Everything that can refuse the save runs first — the
   * constraint pre-flight and the rename warning — so the write sequence below
   * it is only ever entered once the answer is yes.
   */
  private async applyTableEdit(tableId: string, name: string, columns: ColumnSpec[], workspaceTables: Table[]): Promise<boolean> {
    const ctx = await getContext();
    const existing = await ctx.store.tables.findOne(tableId);
    const blocked = await this.constraintPreflight(tableId, columns, existing);
    if (blocked) {
      this.errorMsg = blocked;
      return false;
    }
    const keptOrig = new Set(this.columns.map((c) => c.origField).filter((f): f is string => !!f));
    const changes = planColumnChanges(existing, columns, keptOrig);
    const renames = this.fieldRenames();
    const oldName = existing?.name;
    const renamed = oldName !== undefined && oldName !== name;

    // A rename breaks every name-based reference to this table — projections
    // bind to their sources by name, and so do view instances. Say what will be
    // affected BEFORE writing anything, and let the user back out; the
    // references are then carried across below rather than left dangling.
    let refs: TableReferences | null = null;
    if (existing && renamed) {
      const views = (await ctx.store.viewInstances.find()).filter((v) => v.workspaceId === ctx.workspaceId);
      refs = findTableReferences(existing.name, workspaceTables, views, tableId);
      const what = describeReferences(refs);
      if (what && !(await confirmRename(existing.name, name, what))) return false;
    }

    const patch: Partial<Table> = {
      name,
      title: this.tableTitle.trim(),
      columns,
      readonly: this.tableReadonly,
      updatedAt: Date.now(),
      ...conditionalPatch(existing, changes, this.filters, renames),
    };
    const stages: SaveStage[] = [
      ['the column settings', () => ctx.store.tables.patch(tableId, patch)],
      ['the row data', () => this.migrateRows(tableId, renames, changes.purgeFields, !!existing?.source)],
      // A renamed FIELD is name-bound too: a projection names its output fields,
      // the source fields it reads, and its join keys. Runs before the table
      // rename is carried across, because the specs still say the old NAME here.
      ['renamed fields in projections and views', () => repointFieldRenames(tableId, oldName ?? name, renames, workspaceTables)],
    ];
    if (renamed) stages.push(['references to the old table name', () => repointReferences(tableId, oldName, name, refs)]);
    return this.runStages(stages);
  }

  /**
   * Block the save when a column has just been flagged unique or notnull (or
   * given a tighter `max`) and the rows already there would violate it. The
   * message lists the offending rows so the user can fix the data first.
   *
   * Returns the banner text, or null when the save may go ahead.
   */
  private async constraintPreflight(tableId: string, columns: ColumnSpec[], existing: Table | null): Promise<string | null> {
    const tightened = tightenedConstraints(columns, existing?.columns ?? []);
    if (tightened.length === 0) return null;
    const ctx = await getContext();
    const rows = await ctx.store.rows(tableId).find();
    const violations = scanConstraintViolations(tightened, rows);
    return violations.length > 0 ? constraintErrorMessage(violations) : null;
  }

  /**
   * Re-key renamed fields and scrub deleted ones out of every row.
   *
   * Deleted columns must not be stored, synced or persisted — only their name
   * is remembered (in `deletedColumns`) so a refresh or re-import won't re-add
   * them. Renamed fields must move with their values, or the new column reads
   * undefined and the old value lingers under the stale key forever.
   *
   * `sourced` tables are skipped: their rows are derived or remote (a
   * projection computes them; Datasette owns them), so there is nothing here to
   * re-key — and attempting it would throw from a read-only row collection.
   */
  private async migrateRows(tableId: string, renames: readonly FieldRename[], purgeFields: readonly string[], sourced: boolean): Promise<void> {
    if (sourced || (purgeFields.length === 0 && renames.length === 0)) return;
    const ctx = await getContext();
    const rows = await ctx.store.rows(tableId).find();
    for (const r of rows) {
      const data = migrateRowData(r.data, renames, purgeFields);
      // Null means the row carries none of the affected fields — don't write it.
      if (data) await ctx.store.rows(tableId).patch(r.id, { data, updatedAt: Date.now() });
    }
  }

  /**
   * The funnel for one column: shown only where a filter exists, blue while it
   * is on and faded once it is off. A column with no filter renders an empty
   * cell — a funnel offering nothing to switch would be worse than no funnel.
   *
   * The filter is keyed by the field name as SAVED, so a pending rename must not
   * move the lookup: `origField` is the key, `field` is only what is on screen.
   */
  private renderFilterState(c: ColumnRow) {
    const key = c.origField ?? c.field;
    const active = this.filters[key];
    const stashed = this.filtersOff[key];
    const expr = active ?? stashed;
    if (expr === undefined) return html`<span></span>`;
    const on = active !== undefined;
    return html`<button
      type="button"
      class=${`icon-btn filter-btn ${on ? 'on' : 'off'}`}
      data-testid=${`filter-state-${key}`}
      title=${on ? `Filtered: ${expr} — click to switch this filter off (applied on Save)` : `Filter switched off: ${expr} — click to switch it back on`}
      @click=${() => this.toggleFilter(key)}
    >
      <span class="mi sm">${on ? 'filter_alt' : 'filter_alt_off'}</span>
    </button>`;
  }

  /**
   * The live preview, over the columns as they stand in the editor RIGHT NOW -
   * every keystroke, every renderer pick, every script edit.
   *
   * The draft rows are mapped through `buildColumnSpec`, the same function Save
   * uses, so the preview cannot show something a save would not produce. That is
   * also what carries `script`, `validate` and `renderer` down to it.
   */
  private renderPreview() {
    // The preview rows are keyed by the field names as SAVED, but the columns
    // below read the names the user has typed. Re-key a copy exactly the way
    // `submit` will on save, so a pending rename previews its real values
    // instead of an empty column (and its constraints are checked against them).
    const renames = this.fieldRenames();
    const rows = renames.length > 0 ? this.previewRows.map((r) => ({ ...r, data: renameRowFields(r.data, renames) ?? r.data })) : this.previewRows;
    // Mirror the real grid: hidden columns are excluded from the preview.
    const columns = this.columns.filter((c) => !c.hidden).map(buildColumnSpec);
    return html`<column-preview-table .columns=${columns} .rows=${rows} .state=${this.previewState} .error=${this.previewError} note="Your column changes still save."></column-preview-table>`;
  }

  /**
   * The columns removed from this table before, offered back.
   *
   * The app already remembers their names — that is what stops a refresh from
   * re-adding them — so the only thing missing was showing them. Restoring one
   * brings the COLUMN back, not its values: deleting it purged them from every
   * local row, which the hint says out loud. A source-backed table is the
   * exception, and the wording covers it: the values come back on the next
   * refresh, because they were never ours to delete.
   */
  private renderDeleted() {
    if (this.mode !== 'edit' || this.deletedFields.length === 0) return '';
    return html`<div class="deleted-cols">
      <span class="hint">Removed earlier:</span>
      ${this.deletedFields.map((f) => html`<button type="button" class="add" title=${`Add the column "${f}" back`} @click=${() => this.restoreColumn(f)}>↩ ${f}</button>`)}
      <div class="hint">These are not re-added by a refresh. Restoring one adds the column back empty — its values were removed with it, unless the table refreshes from a source.</div>
    </div>`;
  }

  /**
   * Put a removed column back as a new row in the editor, and stop offering it.
   *
   * Nothing is written here: on save the field is in the table's columns again, so
   * the recomputation of `deletedColumns` drops the name by itself. Backing out of
   * the dialog therefore restores nothing, which is what Cancel should mean.
   */
  private restoreColumn(field: string): void {
    this.deletedFields = this.deletedFields.filter((f) => f !== field);
    if (this.columns.some((c) => c.field.trim().toLowerCase() === field.toLowerCase())) return;
    this.columns = [...this.columns, { field, label: field, type: 'string' }];
  }

  /**
   * Pending field renames: the draft's `origField` (the name as SAVED) paired
   * with the name the user has typed. One source of truth for all three
   * consumers — the save-time row re-key, the live preview's re-key, and the
   * hint below the column list — so they can never disagree about what counts
   * as a rename. Empty in `new` mode, where no row data exists yet.
   */
  private fieldRenames(): FieldRename[] {
    if (this.mode !== 'edit') return [];
    return this.columns.filter((c) => c.origField && c.origField !== c.field.trim()).map((c) => ({ from: c.origField as string, to: c.field.trim() }));
  }

  private renameDetected(): boolean {
    return this.fieldRenames().length > 0;
  }

  /**
   * Bring a new error message into view.
   *
   * The banner is at the top of the body and the body SCROLLS, so a user who was
   * looking at the twentieth column would be told about the first one off screen.
   * Only on the transition to a message — re-scrolling on every render would fight
   * whoever is scrolling.
   */
  override updated(changed: Map<string, unknown>): void {
    if (!changed.has('errorMsg') || !this.errorMsg) return;
    this.renderRoot.querySelector('.editor-pane')?.scrollTo({ top: 0 });
  }

  override render() {
    const title = this.mode === 'edit' ? 'Edit columns' : 'New table';
    const submitLabel = this.mode === 'edit' ? 'Save' : 'Create';
    return html`
      <dialog @cancel=${this.close} @keydown=${ctrlEnterSubmits}>
        <button type="button" class="close-x" title="Close" @click=${this.close}>
          <span class="mi sm">close</span>
        </button>
        <form @submit=${this.submit}>
          <div class="dialog-header">
            <h2>${title}</h2>
            <div class="header-actions">
              <button type="button" class="ghost" @click=${this.close}>Cancel</button>
              <button type="submit" class="primary">${submitLabel}</button>
            </div>
          </div>
          <div class="dialog-body">
            ${this.errorMsg ? html`<div class="error" role="alert" data-testid="editor-error">${this.errorMsg}</div>` : ''} ${this.noticeMsg ? html`<div class="notice">${this.noticeMsg}</div>` : ''}
            <div class="editor-pane">
              <label>
                Name
                <input type="text" autofocus .value=${this.name} @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)} />
              </label>
              <label>
                Title <span style="color:#9ca3af">(optional — shown in the window title)</span>
                <input type="text" .value=${this.tableTitle} @input=${(e: Event) => (this.tableTitle = (e.target as HTMLInputElement).value)} />
              </label>
              <label class="inline">
                <input type="checkbox" data-testid="table-readonly" .checked=${this.tableReadonly} @change=${(e: Event) => (this.tableReadonly = (e.target as HTMLInputElement).checked)} />
                Read-only
                <span style="color:#9ca3af">(show values, no editing or add/delete row)</span>
              </label>

              <div class="columns">
                <div class="col-header">
                  <span></span>
                  ${this.renderFlagHead('visible', '👁', 'Visible')}
                  <span>Field</span>
                  <span>Label</span>
                  <span>Type</span>
                  <span>Renderer</span>
                  <span class="flag-label" title="Script — render(row) computes what the column displays"><span class="mi sm">edit</span></span>
                  <span class="flag-label">Max</span>
                  <span class="flag-label" title="Validation — validate(value, row) throws to reject a manual cell edit"><span class="mi sm">rule</span></span>
                  ${this.renderFlagHead('unique', 'U', 'Unique')} ${this.renderFlagHead('notnull', '!', 'Not null')} ${this.renderFlagHead('sortable', '⇅', 'Sortable')}
                  ${this.renderFlagHead('filterable', '⚲', 'Filterable (includes search)')}
                  <span class="flag-label filter-head" title="Shows a funnel on every column that has a filter. Click one to switch that filter off."><span class="mi sm">filter_alt</span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                ${this.columns.map((c, i) => {
                  const isSrc = this.dragSrcIdx === i;
                  const isTgt = this.dropTargetIdx === i;
                  const edgeClass = isTgt && this.dropEdge === 'before' ? ' drop-before' : isTgt && this.dropEdge === 'after' ? ' drop-after' : '';
                  return html`
                    <div
                      class=${`col-row${isSrc ? ' drag-source' : ''}${edgeClass}`}
                      @dragover=${(e: DragEvent) => this.onRowDragOver(e, i, e.currentTarget as HTMLElement)}
                      @dragleave=${() => this.onRowDragLeave(i)}
                      @drop=${(e: DragEvent) => this.onRowDrop(e, i)}
                    >
                      <span class="drag-handle" title="Drag to reorder" draggable="true" @dragstart=${(e: DragEvent) => this.onRowDragStart(e, i)} @dragend=${() => this.onRowDragEnd()}>
                        <span class="mi sm">drag_indicator</span>
                      </span>
                      <span class="flag">
                        <input
                          type="checkbox"
                          title="Visible — uncheck to hide the column without losing its data"
                          .checked=${!c.hidden}
                          @change=${(e: Event) => this.patchColumn(i, { hidden: !(e.target as HTMLInputElement).checked })}
                        />
                      </span>
                      <input
                        type="text"
                        title="Field — the key this column is stored under in each row"
                        .value=${c.field}
                        @input=${(e: Event) => this.patchColumn(i, { field: (e.target as HTMLInputElement).value })}
                      />
                      <input
                        type="text"
                        title="Label — the heading shown above the column"
                        .value=${c.label}
                        @input=${(e: Event) => this.patchColumn(i, { label: (e.target as HTMLInputElement).value })}
                      />
                      <select
                        .value=${c.type}
                        @change=${(e: Event) =>
                          this.patchColumn(i, {
                            type: (e.target as HTMLSelectElement).value as ColumnType,
                          })}
                      >
                        ${TYPE_OPTIONS.map((t) => html`<option value=${t} ?selected=${t === c.type}>${t}</option>`)}
                      </select>
                      <select
                        title="Renderer — how cells in this column display. Read-only HTML-encoded text when blank."
                        .value=${c.renderer ?? ''}
                        @change=${(e: Event) => {
                          const v = (e.target as HTMLSelectElement).value;
                          this.patchColumn(i, { renderer: v || undefined });
                        }}
                      >
                        <option value="" ?selected=${!c.renderer}>— none —</option>
                        ${rendererOptionsFor(this.rendererOptions, c.renderer).map((r) => html`<option value=${r} ?selected=${r === c.renderer}>${r}</option>`)}
                      </select>
                      <button
                        type="button"
                        class=${`icon-btn script-btn is-${scriptState(c.script, c.scriptActive)}`}
                        title=${SCRIPT_TITLE[scriptState(c.script, c.scriptActive)]}
                        @click=${() => this.editScript(i)}
                      >
                        <span class="mi sm">edit</span>
                      </button>
                      <input
                        type="number"
                        min="0"
                        placeholder="—"
                        title="Max length (strings) or max value (numbers)"
                        .value=${c.max == null ? '' : String(c.max)}
                        @input=${(e: Event) => {
                          const v = (e.target as HTMLInputElement).value;
                          this.patchColumn(i, { max: v === '' ? undefined : Number(v) });
                        }}
                      />
                      <button
                        type="button"
                        class=${`icon-btn validate-btn is-${scriptState(c.validate, c.validateActive)}`}
                        title=${VALIDATE_TITLE[scriptState(c.validate, c.validateActive)]}
                        @click=${() => this.editValidate(i)}
                      >
                        <span class="mi sm">rule</span>
                      </button>
                      <span class="flag">
                        <input type="checkbox" title="Unique" .checked=${!!c.unique} @change=${(e: Event) => this.patchColumn(i, { unique: (e.target as HTMLInputElement).checked })} />
                      </span>
                      <span class="flag">
                        <input type="checkbox" title="Not null" .checked=${!!c.notnull} @change=${(e: Event) => this.patchColumn(i, { notnull: (e.target as HTMLInputElement).checked })} />
                      </span>
                      <span class="flag">
                        <input
                          type="checkbox"
                          title="Sortable — uncheck to disable sorting on this column"
                          .checked=${c.sortable !== false}
                          @change=${(e: Event) =>
                            this.patchColumn(i, {
                              sortable: (e.target as HTMLInputElement).checked ? undefined : false,
                            })}
                        />
                      </span>
                      <span class="flag">
                        <input
                          type="checkbox"
                          title="Filterable — uncheck to disable filtering and search on this column"
                          .checked=${c.filterable !== false}
                          @change=${(e: Event) =>
                            this.patchColumn(i, {
                              filterable: (e.target as HTMLInputElement).checked ? undefined : false,
                            })}
                        />
                      </span>
                      ${this.renderFilterState(c)}
                      <button type="button" class="icon-btn" title="Move up" ?disabled=${i === 0} @click=${() => this.moveColumn(i, -1)}>
                        <span class="mi sm">arrow_upward</span>
                      </button>
                      <button type="button" class="icon-btn" title="Move down" ?disabled=${i === this.columns.length - 1} @click=${() => this.moveColumn(i, 1)}>
                        <span class="mi sm">arrow_downward</span>
                      </button>
                      <button type="button" class="icon-btn row-del" title="Remove column" @click=${() => this.removeColumn(i)}>
                        <span class="mi sm">delete</span>
                      </button>
                    </div>
                  `;
                })}
              </div>

              <button type="button" class="add" @click=${this.addColumn}>+ Add column</button>
              ${this.columnActions.map((a) => html`<button type="button" class="add" title=${a.tooltip ?? a.label} @click=${() => void this.runColumnAction(a)}>${a.label}</button>`)}
              ${this.renderDeleted()} ${this.renameDetected() ? html`<div class="hint">Existing rows are re-keyed on save, so renamed fields keep their data.</div>` : ''}
            </div>
            ${this.mode === 'edit' ? this.renderPreview() : ''}
          </div>
        </form>
      </dialog>
    `;
  }
}

/**
 * Returns a list of "Row N: <reason>" strings for any row that violates one
 * of the supplied (presumed newly-enabled) constraints. Empty list means
 * the constraints can be applied cleanly.
 *
 * The rules themselves live in `table/validate-rules.ts`, shared with the footer's
 * Validate button — two definitions of what `max` means was one too many. Scripts
 * are deliberately not run here: a Save must not be the first thing in this app to
 * run a column script over every row.
 */
function scanConstraintViolations(specs: ColumnSpec[], rows: Row[]): string[] {
  const validator = createValidator(specs);
  return issueMessages(rows.flatMap((r, i) => validator.check(r, i)));
}

declare global {
  interface HTMLElementTagNameMap {
    'new-table-dialog': NewTableDialog;
  }
}
